import { useEffect, useRef, type HTMLAttributes, type Ref } from 'react';
import { ImageIcon } from 'lucide-react';
import {
  websiteMediaFrameStyle,
  type WebsiteMediaFrame,
  type WebsiteMediaFrameSize,
  type WebsiteMediaKind,
} from '@dukanti/shared';
import { cn } from '@/lib/utils';
import { useMediaNaturalSize } from '../../hooks/use-media-natural-size';

/** What the replica shows: the stored file through the same proxy URL as the storefront. */
export interface FramingReplicaMedia {
  readonly kind: WebsiteMediaKind;
  readonly src: string;
  /** The cover of a video (shown while it is paused, and when it cannot play). */
  readonly posterSrc: string | null;
}

export interface FramingReplicaProps {
  /** Width / height of the rendered frame on the device being edited. */
  readonly aspect: number;
  /** `null`: nothing to show (an empty slot). */
  readonly media: FramingReplicaMedia | null;
  readonly frame: WebsiteMediaFrame;
  readonly alt: string;
  readonly emptyLabel: string;
  readonly boxRef?: Ref<HTMLDivElement>;
  /** Pointer, key and ARIA props of the interactive frame. */
  readonly boxProps?: HTMLAttributes<HTMLDivElement>;
  readonly onNaturalSizeChange?: (size: WebsiteMediaFrameSize | null) => void;
}

/** The tallest the frame gets (CSS length). */
const FRAME_MAX_HEIGHT = 'min(20rem, 36dvh)';

/** The media fills the frame exactly: the emitter's `transform-origin` is relative to it. */
const MEDIA_CLASS = 'pointer-events-none block h-full w-full select-none object-cover';

/**
 * Epic #228 P6: the true frame of a media slot (its registry aspect on one
 * device), with the picture styled by `websiteMediaFrameStyle`, the same
 * emitter the storefront uses, so the replica shows what shoppers will see.
 * The frame is always left-to-right: framing `x` is physical (PD1).
 *
 * A video is shown paused on its cover (`preload="metadata"`, muted, no
 * controls); when this browser cannot read it, the cover image stands in.
 */
export function FramingReplica({
  aspect,
  media,
  frame,
  alt,
  emptyLabel,
  boxRef,
  boxProps,
  onNaturalSizeChange,
}: FramingReplicaProps): React.JSX.Element {
  const videoSrc = media?.kind === 'video' ? media.src : null;
  const video = useMediaNaturalSize(videoSrc);
  const showVideo = videoSrc !== null && !video.failed;
  const imageSrc = media === null ? null : media.kind === 'video' ? media.posterSrc : media.src;
  const image = useMediaNaturalSize(showVideo ? null : imageSrc);
  const natural = showVideo ? video.size : image.size;
  const width = natural?.width ?? 0;
  const height = natural?.height ?? 0;

  const reportRef = useRef(onNaturalSizeChange);
  reportRef.current = onNaturalSizeChange;
  useEffect(() => {
    reportRef.current?.(width > 0 && height > 0 ? { width, height } : null);
  }, [height, width]);

  const style = websiteMediaFrameStyle(frame);
  const { className, ...rest } = boxProps ?? {};

  return (
    // The frame is at most about a third of the viewport tall, whatever its
    // aspect, so the controls and the dialog's status stay in view (UI/UX P2-1).
    <div className="mx-auto w-full" style={{ maxWidth: `calc(${aspect} * ${FRAME_MAX_HEIGHT})` }}>
      <div
        ref={boxRef}
        dir="ltr"
        data-testid="framing-replica"
        data-aspect={aspect}
        {...rest}
        className={cn(
          'border-border bg-admin-surface-muted relative overflow-hidden rounded-lg border',
          className,
        )}
        style={{ aspectRatio: String(aspect) }}
      >
        {showVideo && videoSrc !== null ? (
          <video
            key={videoSrc}
            src={videoSrc}
            poster={media?.posterSrc ?? undefined}
            muted
            playsInline
            preload="metadata"
            aria-hidden="true"
            data-testid="framing-video"
            className={MEDIA_CLASS}
            style={style}
            onLoadedMetadata={video.onVideoMetadata}
            onError={video.onError}
          />
        ) : imageSrc !== null ? (
          <img
            key={imageSrc}
            src={imageSrc}
            alt={alt}
            draggable={false}
            data-testid="media-preview"
            className={MEDIA_CLASS}
            style={style}
            onLoad={image.onImageLoad}
            onError={image.onError}
          />
        ) : (
          <span className="text-muted-foreground absolute inset-0 flex flex-col items-center justify-center gap-1 text-xs">
            <ImageIcon aria-hidden="true" className="size-6" />
            {emptyLabel}
          </span>
        )}
      </div>
    </div>
  );
}
